import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Clock, User, Mail, Phone, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import PublicNavbar from "../components/PublicNavbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Booking = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    date: "",
    time: "",
    notes: ""
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await fetch(`${API}/booking/services`);
      if (response.ok) {
        const data = await response.json();
        setServices(data || []);
      }
    } catch {
      // Silent fail - services still displayed from cache
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedService) {
      toast.error("Please select a service");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/booking/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service_id: selectedService.id,
          origin_url: window.location.origin,
          ...form
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          // Redirect to Stripe for deposit payment
          window.location.href = data.checkout_url;
        } else {
          toast.success("Booking confirmed!");
          navigate(`/booking/confirmation?id=${data.booking_id}`);
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || "Booking failed");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Generate time slots
  const timeSlots = [];
  for (let h = 9; h <= 18; h++) {
    timeSlots.push(`${h.toString().padStart(2, "0")}:00`);
    if (h < 18) timeSlots.push(`${h.toString().padStart(2, "0")}:30`);
  }

  // Get minimum date (tomorrow)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split("T")[0];

  return (
    <div className="min-h-screen bg-[#0A0A0B] relative overflow-hidden">
      <PublicNavbar />

      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#D4AF37]/3 rounded-full blur-3xl"></div>
      </div>

      <main className="relative z-10 pt-28 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-[#FAFAFA] mb-4">
              Book a <span className="text-[#D4AF37]">Session</span>
            </h1>
            <p className="text-[#A1A1AA] text-lg">
              Select a service and schedule your appointment
            </p>
          </div>

          {/* Service Selection */}
          <div className="mb-10">
            <h2 className="text-xl font-semibold text-[#FAFAFA] mb-4">Select Service</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(services || []).map((service) => (
                <Card
                  key={service.id}
                  className={`cursor-pointer transition-all ${
                    selectedService?.id === service.id
                      ? "border-[#D4AF37] bg-[#D4AF37]/10"
                      : "border-[#27272A] bg-[#121214] hover:border-[#D4AF37]/50"
                  }`}
                  onClick={() => setSelectedService(service)}
                  data-testid={`service-${service.id}`}
                >
                  <CardContent className="p-5">
                    <h3 className="text-lg font-semibold text-[#FAFAFA] mb-2">{service.name}</h3>
                    <p className="text-[#D4AF37] font-bold">
                      {service.deposit > 0 ? `$${service.deposit} deposit` : "Free"}
                    </p>
                    <p className="text-xs text-[#71717A] mt-1">
                      {service.duration} min
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Booking Form */}
          <div className="bg-[#121214] border border-[#27272A] rounded-xl p-8">
            <h2 className="text-xl font-semibold text-[#FAFAFA] mb-6">Your Details</h2>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-[#A1A1AA]">Full Name *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                    <Input
                      name="customer_name"
                      value={form.customer_name}
                      onChange={handleChange}
                      required
                      className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      placeholder="John Doe"
                      data-testid="booking-name"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[#A1A1AA]">Email *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                    <Input
                      name="customer_email"
                      type="email"
                      value={form.customer_email}
                      onChange={handleChange}
                      required
                      className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      placeholder="you@example.com"
                      data-testid="booking-email"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[#A1A1AA]">Phone</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                    <Input
                      name="customer_phone"
                      type="tel"
                      value={form.customer_phone}
                      onChange={handleChange}
                      className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      placeholder="+1 (555) 000-0000"
                      data-testid="booking-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[#A1A1AA]">Preferred Date *</Label>
                  <div className="relative mt-1">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                    <Input
                      name="date"
                      type="date"
                      value={form.date}
                      onChange={handleChange}
                      required
                      min={minDate}
                      className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      data-testid="booking-date"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[#A1A1AA]">Preferred Time *</Label>
                  <div className="relative mt-1">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                    <select
                      name="time"
                      value={form.time}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-3 py-2 bg-[#1A1A1D] border border-[#27272A] rounded-md text-[#FAFAFA]"
                      data-testid="booking-time"
                    >
                      <option value="">Select time</option>
                      {timeSlots.map((slot) => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-[#A1A1AA]">Additional Notes</Label>
                <div className="relative mt-1">
                  <Textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA] min-h-[100px]"
                    placeholder="Any specific requests or information..."
                    data-testid="booking-notes"
                  />
                </div>
              </div>

              {selectedService && (
                <div className="bg-[#1A1A1D] border border-[#27272A] rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-[#FAFAFA] font-semibold">{selectedService.name}</p>
                      <p className="text-sm text-[#71717A]">{selectedService.duration} minutes</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#D4AF37] font-bold text-lg">
                        {selectedService.deposit > 0 ? `$${selectedService.deposit}` : "Free"}
                      </p>
                      {selectedService.deposit > 0 && (
                        <p className="text-xs text-[#71717A]">deposit required</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !selectedService}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8952F] text-[#0A0A0B] font-semibold hover:from-[#E5C558] hover:to-[#D4AF37] py-6"
                data-testid="booking-submit"
              >
                {loading ? "Processing..." : selectedService?.deposit > 0 ? "Continue to Payment" : "Confirm Booking"}
              </Button>
            </form>
          </div>
        </div>
      </main>

      <footer className="relative z-10 text-center py-8 border-t border-[#27272A]">
        <p className="text-[#71717A] text-xs">
          © 2025 DopeInHD Incorporated. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default Booking;
